import { useEffect, useRef } from "react";

export interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  fbclid?: string;
  referrer?: string;
  landing_page?: string;
}

const UTM_STORAGE_KEY = "prusa_utm_params";

/**
 * Captures UTM parameters from the URL on first visit and persists them
 * in sessionStorage so they survive navigation across the SPA.
 */
export function captureUTMParams(): void {
  // Only capture once per session
  if (sessionStorage.getItem(UTM_STORAGE_KEY)) return;

  const params = new URLSearchParams(window.location.search);
  const utm: UTMParams = {
    landing_page: window.location.pathname,
    referrer: document.referrer || undefined,
  };

  const keys: (keyof UTMParams)[] = [
    "utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content",
  ];

  for (const key of keys) {
    const value = params.get(key);
    if (value) utm[key] = value;
  }

  // Also capture click IDs
  const gclid = params.get("gclid");
  if (gclid) utm.gclid = gclid;
  const fbclid = params.get("fbclid");
  if (fbclid) utm.fbclid = fbclid;

  sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utm));
}

export function getUTMParams(): UTMParams {
  try {
    const stored = sessionStorage.getItem(UTM_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

/**
 * Hook that captures UTM params on mount (once per session).
 */
export function useUTMCapture(): void {
  const captured = useRef(false);
  useEffect(() => {
    if (!captured.current) {
      captureUTMParams();
      captured.current = true;
    }
  }, []);
}
